'use client';

import { useQuery } from '@tanstack/react-query';

import { getQueryClient } from 'app/get-query-client';
import { useAuthStore } from 'lib/stores/authStore';
import type { Rail, TransferInstrument, TransferStatus, TransferVo, Vendor } from 'models/transfer';
import { getDeposits, getWithdrawals } from 'transport/domains/banking';

// Bank transfer history is two endpoints — deposits (getDeposits) and
// withdrawals (getWithdrawals). React Query owns the fetch; this module also
// holds the pure Transfer data shape and the selectors BanksStore used to
// expose as getters.
export interface BankTransfersResult {
    deposits: TransferVo[];
    withdrawals: TransferVo[];
}

export interface TransferData {
    id: string;
    userId: string;
    bankId: string;
    isDeposit: boolean;
    amount: number;
    fee: number;
    createdTs: string;
    status: TransferStatus;
    returnCode: string;
    returnReason: string;
    failureReason: string;
    failureErrorMessage: { code: string; message: string; details: string } | undefined;
    rail: Rail;
    vendor: Vendor;
    immediateStatus: string;
    immediateAmount: number;
    finalizedTs: string | undefined;
    expectedArrivalTs: string | undefined;
    instrument: TransferInstrument | undefined;
}

// Ported verbatim from Transfer.updateFromVo, minus the store/account back-refs
// (account is resolved separately via mapAccountsByTransferBankId — see
// queries/bankAccounts.ts — at the one call site that needs it).
export function transferFromVo(vo: TransferVo, isDeposit: boolean): TransferData {
    // Wire withdrawals are currently done as deposits with negative amounts.
    const isWireWithdrawal = vo.rail === 'wire' && vo.amount_cents < 0;
    const deposit = isDeposit && !isWireWithdrawal;

    let rail: Rail = '';
    if (deposit && vo.deposit_type) {
        rail = vo.deposit_type;
    } else if (vo.rail) {
        rail = vo.rail;
    }

    return {
        id: vo.id,
        bankId: vo.bank_id,
        userId: vo.user_id,
        isDeposit: deposit && vo.amount_cents >= 0,
        status: vo.status,
        amount: vo.amount_cents,
        fee: vo.fee_cents,
        createdTs: vo.created_ts,
        returnCode: vo.return_code,
        returnReason: vo.return_reason,
        failureReason: vo.failure_reason || '',
        failureErrorMessage: vo.failure_error_message,
        immediateStatus: vo.immediate_status,
        immediateAmount: vo.immediate_amount,
        finalizedTs: vo.finalized_ts,
        expectedArrivalTs: vo.expected_arrival_ts,
        rail,
        vendor: vo.vendor || '',
        instrument: vo.instrument,
    };
}

export function getTransferAmountInDollars(transfer: TransferData): number {
    return (transfer.amount / 100) * (transfer.isDeposit ? 1 : -1);
}

export function getTransferFeeInDollars(transfer: TransferData): number {
    return transfer.fee / 100;
}

export function isBeforeExpectedArrival(expectedArrivalTs: string | undefined): boolean {
    return !!expectedArrivalTs && new Date(expectedArrivalTs).valueOf() > Date.now();
}

export type TransferStatusFields = Pick<TransferData, 'status' | 'isDeposit' | 'expectedArrivalTs' | 'rail'>;

// A withdrawal the vendor accepted but whose funds are still in transit to the
// user. APM (PayPal/Venmo/Cash App) is never en route: 'applied' means the
// wallet is already credited.
export function isWithdrawalEnRoute(transfer: TransferStatusFields): boolean {
    return (
        !transfer.isDeposit &&
        transfer.status === 'applied' &&
        transfer.rail !== 'apm' &&
        isBeforeExpectedArrival(transfer.expectedArrivalTs)
    );
}

// For a withdrawal, 'applied' means the vendor accepted the payout — not that
// funds have reached the user's bank. It reads "On its way" until the expected
// arrival passes, then "Completed"; "Sent" is the fallback when no arrival
// estimate exists (unknown rail). APM completes immediately (wallet credited).
export function translateTransferStatus(transfer: TransferStatusFields): string {
    switch (transfer.status) {
        case 'unknown':
            return 'Unknown';
        case 'pending':
            return transfer.isDeposit ? 'In progress' : 'Processing';
        case 'applied':
            if (transfer.isDeposit) return 'Completed';
            if (isWithdrawalEnRoute(transfer)) return 'On its way';
            if (transfer.rail === 'apm' || transfer.expectedArrivalTs) return 'Completed';
            return 'Sent';
        case 'returned':
            return 'Returned';
        default:
            return 'Failed';
    }
}

export const bankTransfersKeys = {
    all: ['bankTransfers'] as const,
    user: (userId: string | null) => ['bankTransfers', userId] as const,
};

export function fetchBankTransfers(userId: string): Promise<BankTransfersResult> {
    return new Promise((resolve, reject) => {
        let settled = false;
        const settle = (fn: () => void) => {
            if (settled) return;
            settled = true;
            fn();
        };

        // Tracks whether the deposits leg itself reached onSuc/onErr, separately
        // from the overall `settled` flag. The transport's onFinally fires as
        // soon as the outer .then(onSuc) handler returns — which, on success, is
        // immediately after onSuc synchronously kicks off getWithdrawals, well
        // before that nested call resolves. Gating the deposits onFinally
        // rejection on `settled` (the overall promise) instead of this flag
        // would reject a still-in-flight, successful request.
        let depositsSettled = false;
        getDeposits(
            userId,
            (deposits) => {
                depositsSettled = true;
                let withdrawalsSettled = false;
                getWithdrawals(
                    userId,
                    (withdrawals) => {
                        withdrawalsSettled = true;
                        settle(() => resolve({ deposits: deposits ?? [], withdrawals: withdrawals ?? [] }));
                    },
                    (err) => {
                        withdrawalsSettled = true;
                        settle(() => reject(err));
                    },
                    () => {
                        // Withdrawals finalized without onSuc/onErr (e.g. suppressed
                        // forced disconnect): reject so the RQ promise never hangs.
                        if (!withdrawalsSettled) {
                            withdrawalsSettled = true;
                            settle(() => reject(new Error('bank withdrawals request did not settle')));
                        }
                    },
                );
            },
            (err) => {
                depositsSettled = true;
                settle(() => reject(err));
            },
            () => {
                // Deposits finalized without onSuc/onErr (e.g. suppressed forced
                // disconnect): reject so the RQ promise never hangs.
                if (!depositsSettled) {
                    settle(() => reject(new Error('bank deposits request did not settle')));
                }
            },
        );
    });
}

export function bankTransfersQueryOptions(userId: string) {
    return {
        queryKey: bankTransfersKeys.user(userId),
        queryFn: () => fetchBankTransfers(userId),
    };
}

// Imperative refetch for non-hook call sites (e.g. after a withdrawal/mesh
// transfer completes) — bypasses the fresh cache, matching BanksStore.loadTransfers'
// always-refresh intent.
export function refetchBankTransfers(userId: string): Promise<BankTransfersResult> {
    return getQueryClient().fetchQuery({ ...bankTransfersQueryOptions(userId), staleTime: 0 });
}

// True when the query is actually allowed to run. Shared by useBankTransfersQuery
// (as the `enabled` gate) and useBankTransfersData (to know the disabled case
// counts as "loaded", matching BanksStore.loadTransfers' guard paths, which
// explicitly set loadedTransfers = true for logged-out/no-access users instead
// of leaving the UI stuck on a spinner forever).
function useIsBankTransfersQueryEnabled(): boolean {
    const userId = useAuthStore((s) => s.userId);
    const isLoggedInMember = useAuthStore((s) => s.isLoggedInMember);
    // Select the boolean field directly, not `state.derived` (a stable object whose
    // hasTransfersAccess() method reads fresh state via get() at call time) — selecting
    // the object means zustand never sees a changed return value, so the component
    // won't re-render when transfer access is revoked/granted for the same userId
    // (e.g. switching into a no-transfer-scope sub-user), leaving isEnabled stale.
    const hasTransfersAccess = useAuthStore((state) => state.hasTransferAccess);
    return !!userId && isLoggedInMember && hasTransfersAccess;
}

// A disabled query never fetches, so isFetched/isError would otherwise stay
// false forever and consumers gating on "loaded" (e.g.
// DepositFundMethodSelectionStep) would spin indefinitely.
export function getIsLoadedTransfers(params: { isEnabled: boolean; isFetched: boolean; isError: boolean }): boolean {
    return !params.isEnabled || params.isFetched || params.isError;
}

export function useBankTransfersQuery() {
    const userId = useAuthStore((s) => s.userId);
    const enabled = useIsBankTransfersQueryEnabled();

    return useQuery({
        ...bankTransfersQueryOptions(userId ?? ''),
        enabled,
    });
}

function toTransfers(result: BankTransfersResult | undefined): TransferData[] {
    if (!result) return [];
    return result.deposits
        .map((vo) => transferFromVo(vo, true))
        .concat(result.withdrawals.map((vo) => transferFromVo(vo, false)));
}

export function getAllTransfers(transfers: TransferData[]): TransferData[] {
    return transfers.slice().sort((a, b) => new Date(b.createdTs).valueOf() - new Date(a.createdTs).valueOf());
}

export function getDepositTransfers(transfers: TransferData[]): TransferData[] {
    return transfers.filter((transfer) => transfer.isDeposit);
}

function isTransferApplied(transfer: TransferData): boolean {
    return transfer.status === 'applied' || transfer.immediateStatus === 'applied';
}

export function getHasDepositedBefore(transfers: TransferData[]): boolean {
    return getDepositTransfers(transfers).some(isTransferApplied);
}

export function getCryptoDeposits(transfers: TransferData[]): TransferData[] {
    return getDepositTransfers(transfers).filter((transfer) => transfer.rail === 'crypto');
}

// At least one deposit has landed, at some point. portfolioCashCents is
// undefined before the React Query portfolio-current cache is populated —
// treat that as "not yet known" and default to true so callers never
// incorrectly show a deposit-aided flow before data arrives (ported from
// BanksStore.hasLandedFunds).
export function getHasLandedFunds(transfers: TransferData[], portfolioCashCents: number | undefined): boolean {
    const depositedMoney = transfers.some(isTransferApplied);
    if (depositedMoney) return true;
    if (portfolioCashCents === undefined) return true;
    return !isNaN(portfolioCashCents) && portfolioCashCents > 0;
}

// Ported from BanksStore.useDepositAidedFlow. Takes the cross-store balance
// inputs as parameters rather than reaching into UserStore directly.
export function getUseDepositAidedFlow(params: {
    loadedTransfers: boolean;
    transfers: TransferData[];
    portfolioCashCents: number | undefined;
    balance: number;
    loadedBalance: boolean;
    isLoggedInApplicant: boolean;
}): boolean {
    const { loadedTransfers, transfers, portfolioCashCents, balance, loadedBalance, isLoggedInApplicant } = params;
    return (
        (loadedTransfers && !getHasLandedFunds(transfers, portfolioCashCents) && balance === 0 && loadedBalance) ||
        isLoggedInApplicant
    );
}

export interface BankTransfersData {
    transfers: TransferData[];
    allTransfers: TransferData[];
    deposits: TransferData[];
    hasDepositedBefore: boolean;
    cryptoDeposits: TransferData[];
    isLoadedTransfers: boolean;
}

export function useBankTransfersData(): BankTransfersData {
    const { data, isFetched, isError } = useBankTransfersQuery();
    const isEnabled = useIsBankTransfersQueryEnabled();
    // React Query can still return cached data under the same ['bankTransfers', userId]
    // key from an earlier, higher-access session (e.g. full access -> logged back in as
    // a sub-user without transfer scope on the same account). Disabled means "not
    // allowed to see this", not just "don't fetch" — never surface stale cached data.
    const transfers = isEnabled ? toTransfers(data) : [];

    return {
        transfers,
        allTransfers: getAllTransfers(transfers),
        deposits: getDepositTransfers(transfers),
        hasDepositedBefore: getHasDepositedBefore(transfers),
        cryptoDeposits: getCryptoDeposits(transfers),
        isLoadedTransfers: getIsLoadedTransfers({ isEnabled, isFetched, isError }),
    };
}

// Imperative snapshot for non-hook call sites (e.g. "snapshot before this async
// call so a later deposit hasn't already flipped hasDepositedBefore"). Reads
// straight from the RQ cache; returns an empty list (so hasDepositedBefore
// reads false) when nothing has been fetched yet, matching the old store's
// default-empty-array behavior before load.
export function getBankTransfersFromCache(userId: string): TransferData[] {
    const result = getQueryClient().getQueryData<BankTransfersResult>(bankTransfersKeys.user(userId));
    return toTransfers(result);
}

// Optimistically appends a freshly created transfer into the RQ cache so
// allTransfers/hasDepositedBefore reflect it immediately, matching the old
// store's `this.transfers = this.transfers.concat(transfer)`.
export function appendTransferToCache(userId: string, vo: TransferVo, isDeposit: boolean): void {
    getQueryClient().setQueryData<BankTransfersResult>(bankTransfersKeys.user(userId), (old) => {
        const base = old ?? { deposits: [], withdrawals: [] };
        return isDeposit
            ? { ...base, deposits: [...base.deposits, vo] }
            : { ...base, withdrawals: [...base.withdrawals, vo] };
    });
}
